import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloClient, Resolvers } from 'apollo-client';
import { ApolloLink, concat, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { startTracking } from 'common/Analytics/PageViewTracker';
import introspectionResult from 'introspection-result';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { DocumentFocusBackoff } from './DocumentFocusBackoff';
import getCookieByName from '../../libs/utils/manageCookies/getCookieByName';

export default function createBrowserClient({
    endpoint,
    cacheDiv,
    resolvers,
    typeDefs,
}: {
    endpoint: string;
    cacheDiv: string;
    resolvers?: Resolvers | Resolvers[];
    typeDefs?: string | string[];
}) {
    let subscriptionConnectionEstablished = false;

    const subscriptionClient = new SubscriptionClient('wss://' + window.location.host + endpoint, {
        reconnect: true,
        connectionParams: {
            'X-Csrf-Token': getCookieByName('_99csrf', document.cookie),
        },
        lazy: true,
        connectionCallback: () => (subscriptionConnectionEstablished = true),
    });
    // @ts-ignore
    subscriptionClient.maxConnectTimeGenerator.setMin(3000); // https://github.com/apollographql/subscriptions-transport-ws/issues/377

    const httpLink = new HttpLink({
        uri: endpoint,
        credentials: 'same-origin',
        headers: {
            'X-Csrf-Token': getCookieByName('_99csrf', document.cookie),
        },
    });

    const trackerMiddleware = new ApolloLink((operation, forward) => {
        const finishtracking = startTracking(`apollo ${operation.operationName}`);
        const observer = forward!(operation); // eslint-disable-line @typescript-eslint/no-non-null-assertion

        let hasCompleted = false;
        setTimeout(() => {
            if (!hasCompleted) {
                console.error(`apollo never finished ${operation.operationName}`);
                finishtracking(true); // Don't track
            }
        }, 10e3); // 10 seconds

        return observer.map(response => {
            hasCompleted = true;
            finishtracking();
            return response;
        });
    });

    const documentFocusBackoff = new DocumentFocusBackoff();

    // HACK: this is a way to open up & customise the WebSocket reconnect behaviour (we are so sorry)
    window.addEventListener('focus', () => {
        if (subscriptionConnectionEstablished) {
            documentFocusBackoff.reset();

            if (subscriptionClient.status === WebSocket.CLOSED) {
                subscriptionClient['clearTryReconnectTimeout']();
                subscriptionClient['connect']();
            }
        }
    });
    // replace `private backoff` to delay reconnect on unfocused tabs
    subscriptionClient['backoff'] = documentFocusBackoff;

    const wsLink = new WebSocketLink(subscriptionClient);

    const link = split(
        ({ query }) => {
            const def = getMainDefinition(query);
            return def.kind === 'OperationDefinition' && def.operation === 'subscription';
        },
        wsLink,
        concat(trackerMiddleware, httpLink)
    );

    const fragmentMatcher = new IntrospectionFragmentMatcher({
        introspectionQueryResultData: introspectionResult,
    });

    const cache = new InMemoryCache({
        addTypename: true,
        fragmentMatcher,
    });

    const apolloDataNode = document.getElementById(cacheDiv);
    const apolloState =
        apolloDataNode && apolloDataNode.textContent ? JSON.parse(apolloDataNode.textContent) : {};
    cache.restore(apolloState);

    const combinedClient = new ApolloClient({
        link,
        cache,
        defaultOptions: {
            query: {
                fetchPolicy: 'cache-first',
                errorPolicy: 'all',
            },
        },
        ssrForceFetchDelay: 150,
        connectToDevTools: true,
        resolvers,
        typeDefs,
    });

    return { combinedClient, subscriptionClient };
}
